import React, { useEffect, useRef, useState } from 'react';
import { FiLayers, FiCheck, FiSliders, FiUpload, FiX } from 'react-icons/fi';
import { IoIosSave } from 'react-icons/io';
import { uploadProjectFile } from '../utils/conversationApi';
import { ALL_UPLOAD_EXTENSIONS, PROJECT_FILES_UPDATED_EVENT } from './ProjectPanel';
import './ProjectSetupModal.css';

let _fileRowSeq = 0;

const isOsFileDrag = (e) => Array.from(e.dataTransfer.types || []).includes('Files');

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * The new-project pop-up, opened from the Projects pop-up's new-project button. Nothing is
 * created until Done: the name, instructions and files picked here are only
 * held in this pop-up, so closing it (×, or the backdrop while nothing has
 * been entered) leaves nothing behind and asks nothing.
 *
 * Done creates the project with its name and instructions in one request
 * (`onCreate` -- instructions refused by the policy check create nothing),
 * then uploads the files and hands the project to `onCreated` once each one
 * has reached the server. Indexing carries on there; the Knowledge base tab
 * shows it. Everything here can also be changed later from the project panel.
 */
function ProjectSetupModal({ onClose, onCreate, onCreated }) {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [instructionsError, setInstructionsError] = useState(null);
  // Save only locks the text (read-only, in the blue "saved" look) until the
  // box is clicked again -- nothing is sent anywhere. Done creates the
  // project with whatever is in the box, locked or not, and closing without
  // Done throws it away like everything else here.
  const [instructionsLocked, setInstructionsLocked] = useState(false);
  // [{id, file, name, size, status, error}] -- status is 'ready' or
  // 'unsupported' until Done, then 'uploading' -> 'indexing' -> 'done', or 'error'.
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // What Done is doing: null, 'creating' or 'uploading'. The pop-up can't be
  // closed meanwhile -- the project exists from 'creating' on.
  const [phase, setPhase] = useState(null);
  const [formError, setFormError] = useState(null);
  const nameInputRef = useRef(null);
  const instructionsRef = useRef(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const busy = phase !== null;
  const hasInput = Boolean(name.trim() || instructions.trim() || files.length);

  const updateFile = (id, changes) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...changes } : f)));
  };

  const addFiles = (fileList) => {
    const added = Array.from(fileList || []).map((file) => {
      const lowerName = file.name.toLowerCase();
      const supported = ALL_UPLOAD_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
      return {
        id: `f${++_fileRowSeq}`, file, name: file.name, size: file.size,
        status: supported ? 'ready' : 'unsupported',
      };
    });
    if (added.length > 0) setFiles((prev) => [...prev, ...added]);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const lockInstructions = () => {
    if (instructions.trim()) setInstructionsLocked(true);
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  // A stray click outside shouldn't throw away what's been typed or picked:
  // the backdrop closes only an untouched pop-up. × always closes.
  const handleBackdropClick = () => {
    if (!hasInput) handleClose();
  };

  // Uploads one picked file into the new project. Resolves true once the
  // server has it (indexing carries on there), false if it failed before that.
  const uploadFile = (projectId, row) => new Promise((resolve) => {
    let settled = false;
    const settle = (reachedServer) => {
      if (settled) return;
      settled = true;
      resolve(reachedServer);
    };
    updateFile(row.id, { status: 'uploading' });
    uploadProjectFile(projectId, row.file, (status) => {
      updateFile(row.id, { status });
      if (status === 'indexing') settle(true);
    })
      .then(() => {
        updateFile(row.id, { status: 'done' });
        settle(true);
      })
      .catch((error) => {
        updateFile(row.id, { status: 'error', error: error.message || 'Upload failed' });
        settle(false);
      })
      .finally(() => {
        // The Knowledge base tab fetched its list before this file existed --
        // and once Done has closed this pop-up, it's the only place left
        // to see the file finish indexing, or fail.
        window.dispatchEvent(
          new CustomEvent(PROJECT_FILES_UPDATED_EVENT, { detail: { project_id: projectId } })
        );
      });
  });

  const handleDone = async () => {
    if (busy) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Give your project a name.');
      nameInputRef.current?.focus();
      return;
    }
    setNameError(null);
    setInstructionsError(null);
    setFormError(null);

    setPhase('creating');
    let project;
    try {
      project = await onCreate(trimmedName, instructions.trim());
    } catch (error) {
      setPhase(null);
      const message = error.message || 'Could not create the project.';
      // Shown by the field it's about: a taken or too-long name, or
      // instructions the policy check refused (422) or couldn't check (503).
      if (error.field === 'name' || error.status === 409 || /project name/i.test(message)) {
        setNameError(message);
        nameInputRef.current?.focus();
      } else if (error.field === 'instructions' || error.status === 422 || error.status === 503) {
        setInstructionsError(message);
        // Open for editing again, so the refused text can be fixed.
        setInstructionsLocked(false);
      } else {
        setFormError(message);
      }
      return;
    }

    const toUpload = files.filter((f) => f.status === 'ready');
    let failedNames = [];
    if (toUpload.length > 0) {
      setPhase('uploading');
      const reachedServer = await Promise.all(toUpload.map((row) => uploadFile(project.project_id, row)));
      failedNames = toUpload.filter((_, i) => !reachedServer[i]).map((row) => row.name);
    }
    onCreated(project, failedNames);
  };

  let footerText = 'Nothing is saved until you click Done. You can change any of this later from the project panel.';
  if (phase === 'creating') footerText = 'Creating your project…';
  if (phase === 'uploading') {
    footerText = 'Uploading your files. The project opens once they reach the server; indexing finishes in the background.';
  }

  return (
    <div className="project-setup-overlay" role="presentation" onClick={handleBackdropClick}>
      <div
        className="project-setup-modal"
        role="dialog"
        aria-modal="true"
        aria-label="New project"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="project-setup-close"
          onClick={handleClose}
          disabled={busy}
          title={busy ? 'Wait for the project to finish being created' : 'Close'}
          aria-label="Close"
        >
          <FiX size={16} />
        </button>

        <div className="project-setup-hero">
          <div className="project-setup-hero-icon"><FiLayers size={22} /></div>
          <div className="project-setup-name-wrap">
            <input
              ref={nameInputRef}
              type="text"
              className={`project-setup-name-input${nameError ? ' has-error' : ''}`}
              value={name}
              placeholder="Name your project"
              onChange={(e) => { setName(e.target.value); if (nameError) setNameError(null); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  instructionsRef.current?.focus();
                }
              }}
              disabled={busy}
              aria-required="true"
              aria-label="Project name (required)"
              title="Name this project"
            />
            <span className="project-setup-name-required" aria-hidden="true">*</span>
          </div>
          {nameError && <div className="project-setup-error project-setup-name-error">{nameError}</div>}
          <p>
            A project keeps its own instructions, knowledge base, and conversation
            history, separate from a regular chat. Add them now, or anytime later
            from the project panel.
          </p>
        </div>

        <div className="project-setup-columns">
          <div className="project-setup-section">
            <h3><FiSliders size={14} /> Instructions</h3>
            <p className="project-setup-section-hint">Applies to every conversation in this project.</p>
            <div className="project-setup-instructions-wrap">
              <textarea
                ref={instructionsRef}
                className={`project-setup-instructions-textarea${instructionsLocked ? ' locked' : ''}`}
                placeholder="e.g. Always cite page numbers. Use metric units. Write in a formal, professional tone."
                value={instructions}
                onChange={(e) => {
                  setInstructions(e.target.value);
                  if (instructionsError) setInstructionsError(null);
                }}
                onClick={() => { if (instructionsLocked && !busy) setInstructionsLocked(false); }}
                onKeyDown={(e) => {
                  // The keyboard way to click a locked box back open.
                  if (instructionsLocked && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setInstructionsLocked(false);
                  }
                }}
                readOnly={instructionsLocked}
                disabled={busy}
                title={instructionsLocked ? 'Click to edit' : undefined}
              />
              <button
                type="button"
                className="project-setup-instructions-save-icon"
                // Keeps the box focused rather than blurring it first.
                onMouseDown={(e) => e.preventDefault()}
                onClick={lockInstructions}
                disabled={busy || instructionsLocked || !instructions.trim()}
                title={instructionsLocked ? 'Click into the box to edit' : 'Save instructions'}
                aria-label="Save instructions"
              >
                <IoIosSave size={14} />
                <span className="project-setup-instructions-save-label">Save</span>
              </button>
            </div>
            {/* Under the box, like the file list under the dropzone, so the
                two boxes start at the same height. */}
            {instructionsError && (
              <div className="project-setup-error project-setup-below-box">{instructionsError}</div>
            )}
            {!instructionsError && instructionsLocked && (
              <div className="project-setup-status project-setup-below-box">
                Saved. It&apos;s added to the project when you click Done.
              </div>
            )}
          </div>

          <div className="project-setup-section">
            <h3><FiUpload size={14} /> Knowledge base</h3>
            <p className="project-setup-section-hint">Every conversation in this project can search these.</p>
            <label
              className={`project-setup-dropzone${dragOver ? ' drag-over' : ''}${busy ? ' disabled' : ''}`}
              onDragOver={(e) => { if (!busy && isOsFileDrag(e)) { e.preventDefault(); setDragOver(true); } }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                if (busy || !isOsFileDrag(e)) return;
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
            >
              <FiUpload size={18} />
              <span>Drop files here, or click to browse</span>
              <input
                type="file"
                multiple
                accept={ALL_UPLOAD_EXTENSIONS.join(',')}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                disabled={busy}
              />
            </label>
            {files.length > 0 && (
              <ul className="project-setup-upload-list">
                {files.map((f) => (
                  <li key={f.id} className={`project-setup-upload-row status-${f.status}`}>
                    <span className="project-setup-upload-name" title={f.name}>{f.name}</span>
                    <span className="project-setup-upload-status">
                      {f.status === 'ready' && formatFileSize(f.size)}
                      {f.status === 'unsupported' && 'Not a supported file type'}
                      {f.status === 'uploading' && 'Uploading…'}
                      {f.status === 'indexing' && 'Indexing…'}
                      {f.status === 'done' && <FiCheck size={14} />}
                      {f.status === 'error' && (f.error || 'Failed')}
                    </span>
                    {!busy && (f.status === 'ready' || f.status === 'unsupported') && (
                      <button
                        type="button"
                        className="project-setup-upload-remove"
                        onClick={(e) => { e.preventDefault(); removeFile(f.id); }}
                        title="Remove"
                        aria-label={`Remove ${f.name}`}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="project-setup-footer">
          {formError
            ? <span className="project-setup-error project-setup-footer-error">{formError}</span>
            : <span className="project-setup-footer-hint">{footerText}</span>}
          <button type="button" className="project-setup-done" onClick={handleDone} disabled={busy}>
            {phase === 'creating' && 'Creating…'}
            {phase === 'uploading' && 'Uploading…'}
            {!busy && 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectSetupModal;
